/**
 * TypeScript port of cms/public/bridge-blocks.js — the canonical Bridge block
 * renderer. The markup, classes, SVGs and CSS here MUST stay byte-for-byte
 * identical to the canonical file so the CMS admin preview equals production.
 *
 * Pure: takes a blocks array, returns an HTML string. The quiz (BridgeScreen)
 * attaches a click handler (event delegation on [data-choice]/[data-cta]) for
 * the interactive Question + CTA blocks.
 *
 * The one intentional difference from the JS source: QUESTION_DEFS is built from
 * the REAL survey question definitions (goals/gender/age) so the Merged Question
 * block surfaces the authoritative choices/values that flow into the survey.
 */
import { BridgeScreenConfig } from "./bridgeContent";
import { Goals } from "../quiz/goals";
import { Gender } from "../quiz/gender";
import { Age } from "../quiz/age";

export type Block = { id?: string; type: string; [k: string]: any };

export interface QuestionChoice {
  value: string;
  emoji: string;
  text: string;
}
export interface QuestionDef {
  title: string;
  choices: QuestionChoice[];
}
export type QuestionDefs = Record<string, QuestionDef>;

export interface RenderOpts {
  questionDefs?: QuestionDefs;
  selectedValue?: string | null;
}

const esc = (v: unknown): string =>
  String(v == null ? "" : v).replace(/[&<>"']/g, (c) => {
    return ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" } as Record<string, string>)[c];
  });

/**
 * QUESTION_DEFS derived from the REAL survey questions so the Merged Question
 * block renders the authoritative choices. `value` MUST equal the survey value
 * (so survey.setValue gets a value SurveyJS recognizes). The goals choices in
 * the survey carry emoji HTML; we strip them down to emoji + plain label here.
 */
export const QUESTION_DEFS: QuestionDefs = {
  goals: {
    title: "What is your main fitness goal right now?",
    choices: [
      { value: Goals.LoseFat, emoji: "\u{1F525}", text: "I’m focused on losing weight" },
      { value: Goals.BuildMuscle, emoji: "\u{1F4AA}", text: "Building muscle is more important than losing fat" },
      { value: Goals.BuildMuscleAndLoseFat, emoji: "\u{1F504}", text: "I’d like to lose fat while I build some muscle" },
    ],
  },
  gender: {
    title: "What gender do you identify with?",
    choices: [
      { value: Gender.Male, emoji: "", text: "Male" },
      { value: Gender.Female, emoji: "", text: "Female" },
      { value: Gender.NonBinary, emoji: "", text: "Non-binary" },
    ],
  },
  age: {
    title: "What is your age range?",
    choices: [
      { value: Age.Teens, emoji: "", text: Age.Teens },
      { value: Age.Twenties, emoji: "", text: Age.Twenties },
      { value: Age.Thirties, emoji: "", text: Age.Thirties },
      { value: Age.Fourties, emoji: "", text: Age.Fourties },
      { value: Age.OverFifty, emoji: "", text: Age.OverFifty },
    ],
  },
};

// Admin-preview copy of the question defs (matches the JS source). The quiz
// passes the real QUESTION_DEFS in via opts to stay authoritative.
const DEFAULT_QUESTION_DEFS: QuestionDefs = {
  goals: {
    title: "What’s your main fitness goal?",
    choices: [
      { value: "Lose Weight", emoji: "\u{1F525}", text: "I’m focused on losing weight" },
      { value: "Lose Fat Most Important", emoji: "\u{1F504}", text: "Lose fat while I build some muscle" },
      { value: "Build Muscle Most Important", emoji: "\u{1F4AA}", text: "Building muscle is more important" },
    ],
  },
  gender: {
    title: "What gender do you identify with?",
    choices: [
      { value: "Male", emoji: "", text: "Male" },
      { value: "Female", emoji: "", text: "Female" },
      { value: "Non-binary", emoji: "", text: "Non-binary" },
    ],
  },
  age: {
    title: "How old are you?",
    choices: [
      { value: "18-29", emoji: "", text: "18–29" },
      { value: "30-39", emoji: "", text: "30–39" },
      { value: "40-49", emoji: "", text: "40–49" },
      { value: "50+", emoji: "", text: "50+" },
    ],
  },
};

const CHECK_SVG =
  '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 12l5 5L19 7" stroke="#fff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/></svg>';
// lets-icons:check-fill — the exact Figma verified badge (filled circle + check).
const BADGE_SVG =
  '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path fill-rule="evenodd" clip-rule="evenodd" d="M12 21C13.1819 21 14.3522 20.7672 15.4442 20.3149C16.5361 19.8626 17.5282 19.1997 18.364 18.364C19.1997 17.5282 19.8626 16.5361 20.3149 15.4442C20.7672 14.3522 21 13.1819 21 12C21 10.8181 20.7672 9.64778 20.3149 8.55585C19.8626 7.46392 19.1997 6.47177 18.364 5.63604C17.5282 4.80031 16.5361 4.13738 15.4442 3.68508C14.3522 3.23279 13.1819 3 12 3C9.61305 3 7.32387 3.94821 5.63604 5.63604C3.94821 7.32387 3 9.61305 3 12C3 14.3869 3.94821 16.6761 5.63604 18.364C7.32387 20.0518 9.61305 21 12 21ZM11.768 15.64L16.768 9.64L15.232 8.36L10.932 13.519L8.707 11.293L7.293 12.707L10.293 15.707L11.067 16.481L11.768 15.64Z" fill="#0B6DFF"/></svg>';

// Highlight: if `highlight` is a substring of `text`, color it inline; else
// render text, then highlight on its own line (legacy bridge behavior).
function renderHeadline(b: Block): string {
  const text = String(b.text || "");
  const esced = esc(text);
  // Inline emphasis: **word** -> blue highlight (multiple, arbitrary spans).
  const inline = esced.replace(/\*\*([^*]+)\*\*/g, '<span class="bb-h__hl">$1</span>');
  if (inline !== esced) return '<h1 class="bb-h">' + inline + "</h1>";
  const hl = String(b.highlight || "");
  if (!hl) return '<h1 class="bb-h">' + esced + "</h1>";
  const idx = text.toLowerCase().indexOf(hl.toLowerCase());
  if (idx >= 0) {
    const before = text.slice(0, idx),
      mid = text.slice(idx, idx + hl.length),
      after = text.slice(idx + hl.length);
    return '<h1 class="bb-h">' + esc(before) + '<span class="bb-h__hl">' + esc(mid) + "</span>" + esc(after) + "</h1>";
  }
  return '<h1 class="bb-h">' + esc(text) + '<span class="bb-h__line"><span class="bb-h__hl bb-h__hl--u">' + esc(hl) + "</span></span></h1>";
}

function renderQuestion(b: Block, opts?: RenderOpts): string {
  const defs = (opts && opts.questionDefs) || DEFAULT_QUESTION_DEFS;
  const def = defs[b.questionName] || DEFAULT_QUESTION_DEFS[b.questionName] || { title: "", choices: [] };
  const selected = opts && opts.selectedValue;
  const rows = def.choices
    .map((c) => {
      const sel = selected != null && selected === c.value;
      return (
        '<button type="button" class="bb-q__opt' +
        (sel ? " is-selected" : "") +
        (c.emoji ? " bb-q__opt--icon" : "") +
        '" data-choice="1" data-question="' +
        esc(b.questionName) +
        '" data-value="' +
        esc(c.value) +
        '">' +
        (c.emoji ? '<span class="bb-q__emo">' + esc(c.emoji) + "</span>" : "") +
        "<span>" +
        esc(c.text) +
        "</span></button>"
      );
    })
    .join("");
  const title = (b as { header?: string }).header || def.title;
  return '<div class="bb-q"><div class="bb-q__title">' + esc(title) + '</div><div class="bb-q__opts">' + rows + "</div></div>";
}

function renderBlock(b: Block, opts?: RenderOpts): string {
  switch (b.type) {
    case "eyebrow":
      return '<div class="bb-eyebrow"><span>' + esc(b.text) + "</span></div>";
    case "headline":
      return renderHeadline(b);
    case "text":
      if (b.withCheck)
        return '<div class="bb-assess"><span class="bb-assess__check">' + CHECK_SVG + "</span><p>" + (b.html || "") + "</p></div>";
      return '<p class="bb-text">' + (b.html || "") + "</p>";
    case "painpoints":
      return (
        '<div class="bb-pp">' +
        ((b.items as Block["items"]) || [])
          .map((p: any) => {
            return (
              '<div class="bb-pp__item ' +
              (p.variant === "positive" ? "is-pos" : "is-neg") +
              '"><span class="bb-pp__emo">' +
              esc(p.emoji) +
              "</span><span>" +
              esc(p.text) +
              "</span></div>"
            );
          })
          .join("") +
        "</div>"
      );
    case "socialproof":
      return (
        '<div class="bb-sp"><span class="bb-sp__emo">' +
        esc(b.emoji) +
        "</span><span><strong>" +
        esc(b.count) +
        "</strong> " +
        esc(b.text) +
        "</span></div>"
      );
    case "testimonial": {
      const verified = b.verified !== false;
      const who =
        "<span>" + esc(b.name) + (verified ? ", Verified Member" : "") + "</span>" +
        (verified ? '<span class="bb-tm__badge">' + BADGE_SVG + "</span>" : "");
      const inner = '<p class="bb-tm__quote">“' + esc(b.quote) + '”</p><div class="bb-tm__who">' + who + "</div>";
      if (b.avatarUrl)
        return '<div class="bb-tm bb-tm--avatar"><div class="bb-tm__ava"><img src="' + esc(b.avatarUrl) + '" alt="' + esc(b.name) + '"/></div><div class="bb-tm__body">' + inner + "</div></div>";
      return '<div class="bb-tm"><div class="bb-tm__body">' + inner + "</div></div>";
    }
    case "image":
      return '<div class="bb-img"><img class="' + (b.rounded ? "is-rounded" : "") + '" src="' + esc(b.url) + '" alt="' + esc(b.alt) + '"/></div>';
    case "question":
      return renderQuestion(b, opts);
    case "cta":
      return (
        '<button type="button" class="bb-cta" data-cta="1"><span>' +
        esc(b.text || "Continue") +
        '</span><svg width="22" height="22" viewBox="0 0 25 24" fill="none" aria-hidden="true"><path d="M8.12 21.01c.49.49 1.28.49 1.77 0l8.31-8.31a.996.996 0 0 0 0-1.41L9.89 2.98a1.25 1.25 0 0 0-1.77 1.77L15.36 12l-7.25 7.25c-.48.48-.48 1.28.01 1.76Z" fill="#fff"/></svg></button>'
      );
    default:
      return "";
  }
}

// Per-block "space below" scale -> px. Lets the manager tune the gap between blocks.
const SPACE_PX: Record<string, number> = { none: 0, xs: 8, sm: 12, md: 20, lg: 32, xl: 48 };

export function renderBlocksHTML(blocks: Block[] | undefined | null, opts?: RenderOpts): string {
  if (!Array.isArray(blocks)) return "";
  return (
    '<div class="bb">' +
    blocks
      .map((b) => {
        const inner = renderBlock(b, opts);
        if (!inner) return "";
        const bb = b as { align?: string; space?: string };
        const align = bb.align === "left" ? "left" : "center";
        const sp = bb.space && SPACE_PX[bb.space] != null ? ` style="margin-bottom:${SPACE_PX[bb.space]}px"` : "";
        return `<div class="bb-item" data-align="${align}"${sp}>${inner}</div>`;
      })
      .join("") +
    "</div>"
  );
}

export const PROGRESS_HEADER_HTML =
  '<div class="ph"><div class="ph__row">' +
  '<div class="ph__icon"><svg width="25" height="24" viewBox="0 0 25 24" fill="none"><circle cx="12.24" cy="12" r="12" fill="#fff"/><circle cx="12.16" cy="11.92" r="10" fill="#0B6DFF"/></svg></div>' +
  '<div class="ph__bar"></div>' +
  '<div class="ph__icon"><svg width="25" height="24" viewBox="0 0 25 24" fill="none"><rect x="0.74" width="24" height="24" rx="12" fill="#fff"/><circle cx="12.7" cy="12" r="6" fill="#cfd6e2"/></svg></div>' +
  '<div class="ph__bar"></div>' +
  '<div class="ph__icon"><svg width="25" height="26" viewBox="0 0 25 26" fill="none"><rect x="0.5" y="1" width="24" height="24" rx="12" fill="#fff"/><path d="M6.8 11.5H18.2M6.8 11.5V18.1c0 .3.2.6.6.6h10.2c.4 0 .6-.3.6-.6V11.5M6.8 11.5V8.8c0-.3.2-.6.6-.6h10.2c.4 0 .6.3.6.6v2.7" stroke="#cfd6e2" stroke-width="1.2"/></svg></div>' +
  "</div><div class=\"ph__labels\"><span>Getting Started</span><span>Goal Timeline</span><span>Personalized Plan</span></div></div>";

export const BLOCKS_CSS = [
  ".bb{display:flex;flex-direction:column;align-items:center;padding:24px 24px 32px;max-width:577px;margin:0 auto;box-sizing:border-box;width:100%;font-family:Lato,sans-serif;color:#0d1220}",
  ".bb-item{width:100%;display:flex;flex-direction:column;align-items:center;margin-bottom:20px}",
  ".bb-item:last-child{margin-bottom:0}",
  ".bb-item[data-align=left]{align-items:flex-start}",
  ".bb-item[data-align=left] .bb-h,.bb-item[data-align=left] .bb-text,.bb-item[data-align=left] .bb-q__title{text-align:left}",
  ".bb-eyebrow{padding:8px 15px;border-radius:100px;background:linear-gradient(270deg,#E7F0FF 3%,#fff 100%)}",
  ".bb-eyebrow span{font-family:Oswald,sans-serif;font-weight:600;font-size:13px;letter-spacing:.04em;color:#0B6DFF;text-transform:uppercase}",
  ".bb-h{font-weight:800;font-size:clamp(33px,6vw,40px);line-height:1.18;letter-spacing:-0.01em;margin:0;text-align:center;text-wrap:balance;max-width:100%}",
  ".bb-h__hl{color:#0B6DFF}.bb-h__line{display:block}.bb-h__hl--u{background-image:linear-gradient(transparent 85%,rgba(11,109,255,.18) 85%);background-repeat:no-repeat;background-size:100% 100%}",
  ".bb-text{font-size:16px;line-height:1.5;color:#4a4a55;margin:0;text-align:center;max-width:500px}.bb-text strong{font-weight:700;color:#1a1f2b}",
  ".bb-assess{display:flex;gap:10px;align-items:flex-start;text-align:left;max-width:430px}.bb-assess__check{flex-shrink:0;width:22px;height:22px;border-radius:7px;background:#0B6DFF;display:flex;align-items:center;justify-content:center;margin-top:1px}.bb-assess__check svg{width:12px;height:12px}.bb-assess p{font-size:14.5px;line-height:1.5;color:#4a4a55;margin:0}.bb-assess strong{color:#1a1f2b}",
  ".bb-pp{display:flex;flex-direction:column;gap:11px;width:100%;max-width:400px}.bb-pp__item{display:flex;align-items:center;gap:12px;padding:13px 16px;border-radius:10px;font-size:15px;line-height:1.4;color:#1a1a1a;text-align:left}.bb-pp__item.is-neg{background:#FEF0EC}.bb-pp__item.is-pos{background:#ECFAEC}.bb-pp__emo{font-size:20px;flex-shrink:0}",
  ".bb-sp{display:flex;gap:6px;justify-content:center;text-align:center;font-size:13px;font-style:italic;line-height:1.6;color:#8a8a9a;max-width:340px}.bb-sp__emo{font-size:18px;font-style:normal}.bb-sp strong{color:#0B6DFF;font-weight:700;font-style:normal}",
  ".bb-tm{padding:24px;border-radius:10px;background:#e9e9ef;width:100%;max-width:577px;box-sizing:border-box;text-align:left}.bb-tm--avatar{display:flex;gap:24px;align-items:center}.bb-tm__ava{flex-shrink:0;width:64px;height:64px;border-radius:50%;overflow:hidden;border:4px solid #fff;box-sizing:border-box}.bb-tm__ava img{width:100%;height:100%;object-fit:cover}.bb-tm__body{min-width:0;display:flex;flex-direction:column;gap:8px}.bb-tm__quote{font-style:italic;font-size:16px;line-height:1.5;color:#1a1a1a;margin:0}.bb-tm__who{display:flex;align-items:center;gap:8px;font-size:16px;font-weight:700;color:#4a4a4a}.bb-tm__badge{display:inline-flex;width:24px;height:24px;flex-shrink:0}.bb-tm__badge svg{width:100%;height:100%;display:block}",
  ".bb-logoheader{display:flex;justify-content:center;align-items:center;padding:0 22px}.bb-logoheader__img{width:290px;max-width:72%;height:auto;display:block}",
  ".bb-img{width:100%;max-width:400px}.bb-img img{display:block;width:100%;height:auto}.bb-img img.is-rounded{border-radius:14px}",
  ".bb-q{width:100%;max-width:577px}.bb-q__title{font-weight:800;font-size:24px;line-height:1.25;margin-bottom:18px;text-align:center}.bb-q__opts{display:flex;flex-direction:column;gap:14px}",
  ".bb-q__opt{display:block;width:100%;text-align:center;padding:14px 16px;border-radius:8px;border:2px solid #F6F6F8;background:#F6F6F8;color:#222;font-family:inherit;font-size:16px;line-height:20px;cursor:pointer;box-sizing:border-box;box-shadow:0 2px 4px rgba(0,0,0,.08);transition:border-color .15s,background-color .15s,color .15s,box-shadow .15s,transform .1s}",
  ".bb-q__opt--icon{display:flex;align-items:center;gap:10px;text-align:left;padding:24px 16px}",
  ".bb-q__opt:hover{border-color:#0B6DFF}.bb-q__opt:active{transform:scale(.97)}.bb-q__opt.is-selected{background:#E7F0FF;border-color:#E7F0FF;color:#0B6DFF;font-weight:700;box-shadow:0 4px 4px rgba(11,109,255,.08) inset}.bb-q__emo{font-size:20px;flex-shrink:0}.bb-q--nudge .bb-q__opt{border-color:#0B6DFF;box-shadow:0 0 0 3px rgba(11,109,255,.18)}",
  ".bb-cta{display:flex;align-items:center;justify-content:center;gap:8px;border:none;background:#0b6dff;font-family:Lato,sans-serif;font-weight:700;font-size:17px;line-height:1.2;color:#fff;padding:17px 24px;border-radius:50px;width:100%;max-width:540px;box-sizing:border-box;cursor:pointer}",
  /* progress header */
  ".ph{padding:16px 22px 18px;border-radius:0 0 20px 20px;background:#F6F6F8;box-shadow:0 2px 4px #00000014}.ph__row{display:flex;align-items:center;gap:2px;margin-bottom:8px}.ph__bar{flex:1;height:10px;background:#fff;border-radius:100px}.ph__icon{width:25px;height:24px;display:flex;align-items:center;justify-content:center;flex-shrink:0}.ph__labels{display:flex;justify-content:space-between}.ph__labels span{font-size:11px;line-height:1.2;flex:1;color:#000;font-family:Lato,sans-serif}.ph__labels span:nth-child(1){text-align:left;font-weight:700}.ph__labels span:nth-child(2){text-align:center}.ph__labels span:nth-child(3){text-align:right}",
  /* MOBILE: scale the logo + testimonial down to the Figma mobile artboard. */
  "@media (max-width:560px){.bb-logoheader__img{width:210px}.bb-tm{padding:12px}.bb-tm--avatar{gap:8px}.bb-tm__ava{width:48px;height:48px;border-width:3px}.bb-tm__body{gap:4px}.bb-tm__quote{font-size:12px}.bb-tm__who{font-size:12px;gap:4px}.bb-tm__badge{width:14px;height:14px}}",
].join("\n");

/**
 * Derive blocks from a legacy BridgeScreenConfig (the bundled-fallback path).
 * Mirrors the worker's configToBlocks (cms/src/index.ts) so the fallback renders
 * the same blocks the CMS would have served.
 */
export function configToBlocks(c: BridgeScreenConfig | null | undefined): Block[] {
  if (!c) return [];
  const blocks: Block[] = [];
  let n = 0;
  const uid = () => `cfg_${n++}`;
  if (c.sectionLabel) blocks.push({ id: uid(), type: "eyebrow", text: c.sectionLabel });
  if (c.headline) blocks.push({ id: uid(), type: "headline", text: c.headline.prefix, highlight: c.headline.highlighted });
  if (c.subheadline) blocks.push({ id: uid(), type: "text", html: c.subheadline, withCheck: false });
  if (c.painPoints && c.painPoints.length) blocks.push({ id: uid(), type: "painpoints", items: c.painPoints });
  if (c.socialProof) blocks.push({ id: uid(), type: "socialproof", ...c.socialProof });
  blocks.push({ id: uid(), type: "cta", text: c.cta || "Continue" });
  return blocks;
}
